import React from 'react';
import {  ArrowUp, ArrowDown, TrendingUp,  CheckCircle, Users, FileText, Filter, Search, MoreVertical } from 'lucide-react';


// Main App Component
export default function ClassroomAI() {
  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans text-slate-800">

      <main className="flex-grow p-4 sm:p-6 lg:p-8">
        <ClassroomAiPage />
      </main>

    </div>
  );
}

// Main Classroom AI Page Component
const ClassroomAiPage = () => {
    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <PageHeader />
            <StatsCards />
            <ClassroomAnalytics />
            <StudentDiagnostics />
            <StudentPerformance />
        </div>
    );
};

// Page Header
const PageHeader = () => (
    <div>
        <h1 className="text-2xl font-bold text-slate-900">Classroom AI</h1>
    </div>
);

// Stats Cards Section
const StatsCards = () => {
    const stats = [
        { icon: FileText, label: "Quiz Score", value: "8.64", change: "+0.2", changeType: "increase", color: "blue" },
        { icon: Users, label: "Class Average", value: "8.64", change: "+0.2", changeType: "increase", color: "green" },
        { icon: CheckCircle, label: "Completion Rate", value: "100.0%", change: "0.0", changeType: "neutral", color: "purple" },
        { icon: TrendingUp, label: "Learning Rate", value: "4", change: "+0.5", changeType: "increase", color: "orange" },
    ];

    const colors = {
        blue: 'text-blue-500',
        green: 'text-green-500',
        purple: 'text-purple-500',
        orange: 'text-orange-500',
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
                <div key={index} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between">
                        <div className={`p-2 bg-slate-100 rounded-lg`}>
                           <stat.icon className={`w-6 h-6 ${colors[stat.color]}`} />
                        </div>
                        <div className="flex items-center text-xs font-semibold">
                            {stat.changeType === 'increase' && <ArrowUp className="w-4 h-4 text-green-500" />}
                            {stat.changeType === 'decrease' && <ArrowDown className="w-4 h-4 text-red-500" />}
                            <span className={stat.changeType === 'increase' ? 'text-green-500' : stat.changeType === 'decrease' ? 'text-red-500' : 'text-slate-500'}>
                                {stat.change}
                            </span>
                        </div>
                    </div>
                    <p className="mt-4 text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
            ))}
        </div>
    );
};

// Classroom Analytics Section
const ClassroomAnalytics = () => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="flex flex-wrap justify-between items-center gap-4">
            <h2 className="text-lg font-bold">Classroom Analytics</h2>
            <div className="flex items-center gap-2">
                <button className="px-4 py-2 text-sm font-semibold bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">View Report</button>
                <button className="p-2 border rounded-lg hover:bg-slate-50"><Filter className="w-5 h-5" /></button>
                <button className="p-2 border rounded-lg hover:bg-slate-50"><Search className="w-5 h-5" /></button>
            </div>
        </div>
        {/* Placeholder for analytics content */}
        <div className="mt-6 text-center text-slate-400">Analytics chart or data would be displayed here.</div>
    </div>
);

// Student-wise Diagnostics Section
const StudentDiagnostics = () => {
    const students = [
        { name: 'Rohan Kumar', diagnosis: 'Conceptual', status: 'Completed' },
        { name: 'Anil Mhase', diagnosis: 'Conceptual', status: 'Completed' },
        { name: 'Sunil Chavan', diagnosis: 'Conceptual', status: 'Completed' },
    ];
    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold">Student-wise Diagnostics</h2>
                <button className="px-4 py-2 text-sm font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">View All Diagnostics</button>
            </div>
            <div className="mt-4 flow-root">
                <div className="-mx-6 -my-2 overflow-x-auto">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-slate-900">Student Name</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Diagnosis</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {students.map((student) => (
                                    <tr key={student.name}>
                                        <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-slate-900">{student.name}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">{student.diagnosis}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-green-600 font-semibold">{student.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Student Performance Table Section
const StudentPerformance = () => {
    const students = [
        { name: 'Rohan Kumar', quizScore: 9.6, classAvg: 8.6, completion: 100, learning: 4.8, status: 'increase' },
        { name: 'Anil Mhase', quizScore: 9.2, classAvg: 8.6, completion: 100, learning: 4.6, status: 'increase' },
        { name: 'Sunil Chavan', quizScore: 8.8, classAvg: 8.6, completion: 100, learning: 4.4, status: 'increase' },
        { name: 'Tejas Patil', quizScore: 8.4, classAvg: 8.6, completion: 100, learning: 4.2, status: 'decrease' },
        { name: 'Kunal Jadhav', quizScore: 8.0, classAvg: 8.6, completion: 100, learning: 4.0, status: 'decrease' },
    ];
    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h2 className="text-lg font-bold">Student Performance</h2>
            <div className="mt-4 flow-root">
                <div className="-mx-6 -my-2 overflow-x-auto">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-slate-900">Student Name</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Quiz Score</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Class Avg.</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Completion</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">Learning</th>
                                    <th scope="col" className="relative py-3.5 pl-3 pr-6"><span className="sr-only">Edit</span></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {students.map((student) => (
                                    <tr key={student.name}>
                                        <td className="whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-slate-900">{student.name}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">{student.quizScore.toFixed(1)}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">{student.classAvg.toFixed(1)}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">{student.completion}%</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500 flex items-center gap-1">
                                            {student.learning.toFixed(1)}
                                            {student.status === 'increase' ? <ArrowUp className="w-4 h-4 text-green-500" /> : <ArrowDown className="w-4 h-4 text-red-500" />}
                                        </td>
                                        <td className="whitespace-nowrap py-4 pl-3 pr-6 text-right text-sm font-medium">
                                            <button className="text-slate-400 hover:text-slate-600"><MoreVertical className="w-5 h-5"/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

